import React from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import './ResponsibilityActionDialog.css';

export type ActionType = 'remove' | 'capacity-change';

export interface ResponsibilityActionDialogProps {
  isOpen: boolean;
  actionType: ActionType;
  userName: string;
  affaireCount: number;
  onClose: () => void;
  onKeep: () => void;
  onRedistribute: () => void;
  isProcessing?: boolean;
}

const ResponsibilityActionDialog: React.FC<ResponsibilityActionDialogProps> = ({
  isOpen,
  actionType,
  userName,
  affaireCount,
  onClose,
  onKeep,
  onRedistribute,
  isProcessing = false
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const getTitle = () => {
    if (actionType === 'remove') {
      return t('admin.userManagement.responsibilities.dialog.removeTitle', 'Retirer un responsable');
    }
    return t('admin.userManagement.responsibilities.dialog.capacityTitle', 'Modifier la capacité');
  };

  const getDescription = () => {
    if (actionType === 'remove') {
      return t(
        'admin.userManagement.responsibilities.dialog.removeDescription',
        `${userName} a actuellement ${affaireCount} dossier(s) actif(s) assigné(s). Que souhaitez-vous faire avec ces dossiers ?`
      );
    }
    return t(
      'admin.userManagement.responsibilities.dialog.capacityDescription',
      `Cette modification de capacité affecte ${affaireCount} dossier(s) actif(s). Voulez-vous rééquilibrer la charge maintenant ou seulement pour les nouveaux dossiers ?`
    );
  };

  const getKeepLabel = () => {
    if (actionType === 'remove') {
      return t('admin.userManagement.responsibilities.dialog.keepAssignments', 'Conserver ses dossiers');
    }
    return t('admin.userManagement.responsibilities.dialog.applyFuture', 'Appliquer aux nouveaux dossiers uniquement');
  };

  const getKeepDescription = () => {
    if (actionType === 'remove') {
      return t(
        'admin.userManagement.responsibilities.dialog.keepDescription',
        'Les dossiers restent assignés mais la personne ne recevra plus de nouveaux dossiers'
      );
    }
    return t(
      'admin.userManagement.responsibilities.dialog.futureDescription',
      'La nouvelle capacité sera utilisée uniquement pour les prochaines assignations'
    );
  };

  const getRedistributeLabel = () => {
    if (actionType === 'remove') {
      return t('admin.userManagement.responsibilities.dialog.redistributeAssignments', 'Redistribuer ses dossiers');
    }
    return t('admin.userManagement.responsibilities.dialog.rebalanceNow', 'Rééquilibrer maintenant');
  };

  const getRedistributeDescription = () => {
    if (actionType === 'remove') {
      return t(
        'admin.userManagement.responsibilities.dialog.redistributeDescription',
        `Les ${affaireCount} dossier(s) seront réassignés aux autres responsables selon leur disponibilité`
      );
    }
    return t(
      'admin.userManagement.responsibilities.dialog.rebalanceDescription',
      'Tous les dossiers actifs seront redistribués selon les nouvelles capacités'
    );
  };

  return (
    <div className="responsibility-action-dialog-overlay">
      <div className="responsibility-action-dialog">
        {/* Header */}
        <div className="responsibility-action-dialog-header">
          <h2 className="responsibility-action-dialog-title">{getTitle()}</h2>
          <button
            onClick={onClose}
            className="responsibility-action-dialog-close"
            disabled={isProcessing}
            aria-label="Fermer"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="responsibility-action-dialog-content">
          <p className="responsibility-action-dialog-description">{getDescription()}</p>

          <div className="responsibility-action-dialog-options">
            {/* Option 1: Keep / Apply Future */}
            <button
              className="responsibility-action-dialog-option"
              onClick={onKeep}
              disabled={isProcessing}
            >
              <div className="responsibility-action-dialog-option-header">
                <div className="responsibility-action-dialog-option-icon responsibility-action-dialog-option-icon--keep">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="responsibility-action-dialog-option-title">{getKeepLabel()}</h3>
              </div>
              <p className="responsibility-action-dialog-option-description">{getKeepDescription()}</p>
            </button>

            {/* Option 2: Redistribute / Rebalance */}
            <button
              className="responsibility-action-dialog-option"
              onClick={onRedistribute}
              disabled={isProcessing}
            >
              <div className="responsibility-action-dialog-option-header">
                <div className="responsibility-action-dialog-option-icon responsibility-action-dialog-option-icon--redistribute">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                    />
                  </svg>
                </div>
                <h3 className="responsibility-action-dialog-option-title">{getRedistributeLabel()}</h3>
              </div>
              <p className="responsibility-action-dialog-option-description">{getRedistributeDescription()}</p>
            </button>
          </div>

          {isProcessing && (
            <div className="responsibility-action-dialog-processing">
              <div className="responsibility-action-dialog-spinner" />
              <p>Traitement en cours...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { ResponsibilityActionDialog };
export default ResponsibilityActionDialog;
